import React, { useState } from "react";
import { MapPin, Phone, Mail, Send, MessageCircle, Clock } from "lucide-react";
import api, { BUSINESS, waLink } from "../lib/api";
import { toast } from "sonner";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { document.title = "Contact — Faisal Car Bazar"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill all fields");
      return;
    }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you! We'll reach out shortly.");
      setForm({ name: "", phone: "", message: "" });
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <div className="mb-12">
        <div className="eyebrow mb-4">Contact Us</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#F5F5DC] font-light">
          Let's talk <span className="text-[#DC143C] italic">cars.</span>
        </h1>
        <div className="luxe-divider" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <h2 className="font-display text-2xl text-[#F5F5DC] mb-6">Visit, call or message us</h2>
          <ul className="space-y-6 text-sm text-[#F5F5DC]/85">
            <li className="flex gap-4">
              <MapPin className="text-[#D4AF37] flex-shrink-0 mt-1" size={20} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0] mb-1">Address</div>
                <div className="leading-relaxed">{BUSINESS.address}</div>
              </div>
            </li>
            <li className="flex gap-4">
              <Phone className="text-[#D4AF37] flex-shrink-0 mt-1" size={20} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0] mb-1">Phone</div>
                <a href={`tel:${BUSINESS.phone}`} className="hover:text-[#D4AF37]">{BUSINESS.phone}</a>
              </div>
            </li>
            <li className="flex gap-4">
              <Mail className="text-[#D4AF37] flex-shrink-0 mt-1" size={20} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0] mb-1">Email</div>
                <a href={`mailto:${BUSINESS.email}`} className="hover:text-[#D4AF37]">{BUSINESS.email}</a>
              </div>
            </li>
            <li className="flex gap-4">
              <Clock className="text-[#D4AF37] flex-shrink-0 mt-1" size={20} />
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-[#A0A0A0] mb-1">Hours</div>
                <div>Open Daily · 10 AM – 9 PM</div>
              </div>
            </li>
          </ul>

          <div className="flex gap-3 mt-8">
            <a
              data-testid="contact-whatsapp"
              href={waLink("Hello Faisal Car Bazar!")}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary"
              style={{ background: "#25D366", borderColor: "#25D366" }}
            >
              <MessageCircle size={16} /> WhatsApp
            </a>
            <a
              data-testid="contact-call"
              href={`tel:${BUSINESS.phone}`}
              className="btn-secondary"
            >
              <Phone size={16} /> Call Now
            </a>
          </div>
        </div>

        <div className="lg:col-span-7">
          <form onSubmit={submit} className="premium-card p-8" data-testid="contact-form">
            <h3 className="font-display text-2xl text-[#F5F5DC] mb-6">Send us a message</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Your Name</label>
                <input
                  data-testid="contact-name"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-[#F5F5DC] focus:border-[#DC143C] outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Phone Number</label>
                <input
                  data-testid="contact-phone"
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-[#F5F5DC] focus:border-[#DC143C] outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.25em] text-[#D4AF37] mb-2">Message</label>
                <textarea
                  data-testid="contact-message"
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full bg-[#1A1A1A] border border-white/10 rounded-lg px-4 py-3 text-[#F5F5DC] focus:border-[#DC143C] outline-none resize-none"
                  required
                />
              </div>
              <button
                type="submit"
                data-testid="contact-submit"
                disabled={loading}
                className="btn-primary w-full justify-center"
              >
                {loading ? "Sending…" : <>Send Message <Send size={16} /></>}
              </button>
            </div>
          </form>
        </div>
      </div>

      <div className="mt-16">
        <div className="map-frame h-[420px]">
          <iframe
            title="Faisal Car Bazar Location"
            src={BUSINESS.mapEmbed}
            width="100%"
            height="100%"
            style={{ border: 0 }}
            loading="lazy"
          />
        </div>
      </div>
    </div>
  );
}
